import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval } from 'rxjs';
import { Engine } from 'noodle/engine/Engine';
import { Scene } from 'noodle/engine/scene/Scene';
import { CompositeSceneRenderer } from 'noodle/engine/renderer/CompositeSceneRenderer';
import { DeferredSceneRenderer } from 'noodle/engine/renderer/DeferredSceneRenderer';
import { RenderMode } from 'noodle/engine/renderer';
import { RenderGroup } from 'noodle/engine/object/RenderGroup';
import { GpuTimingMode } from 'noodle/engine/development/GpuTimer';

type PassRow = {
  name: string;
  cpu: number;
  gpu: number;
  active: boolean;
  // CPU-only rows (Tick, Culling, Instance Prep, Tile Assign) have no GPU work.
  // The HUD renders '—' in the GPU column instead of '0.00' to make it obvious.
  cpuOnly?: boolean;
};

const REFRESH_INTERVAL_MS = 250;
const ROLLING_WINDOW_FRAMES = 30;

type Sample = {
  // Frame setup CPU times — work that runs around the timed render passes.
  // tickCpu covers the engine + scene tick event managers (physics, day-night,
  // animated properties, decals, physics tile streaming). cullingCpu sums all
  // getAllVisibleObjects + getVisibleLights calls in the render path.
  // instancePrepCpu covers instanceManager.prepareFrame + non-instanced sort.
  // tileAssignCpu is a strict subset of lightingCpu (NOT added to cpuTotal).
  tickCpu: number;
  // Strict subsets of tickCpu, sums to tickCpu. Shown indented under Tick in
  // the HUD. NOT added to cpuTotal (parent already counted).
  tickEngineCpu: number;
  tickLightAnimCpu: number;
  tickObjAnimCpu: number;
  tickFeaturesCpu: number;
  tickDecalsCpu: number;
  tickPhysicsCpu: number;
  tickTerrainStreamCpu: number;
  tickObjectStreamCpu: number;
  tickSceneOtherCpu: number;
  cullingCpu: number;
  instancePrepCpu: number;
  tileAssignCpu: number;
  // Per-pass CPU/GPU times in ms.
  shadowCpu: number;
  shadowGpu: number;
  gbufferCpu: number;
  gbufferGpu: number;
  ssaoCpu: number;
  ssaoGpu: number;
  lightingCpu: number;
  lightingGpu: number;
  volumetricCpu: number;
  volumetricGpu: number;
  godRaysCpu: number;
  godRaysGpu: number;
  aerialPerspectiveCpu: number;
  aerialPerspectiveGpu: number;
  cloudVolumesCpu: number;
  cloudVolumesGpu: number;
  transparencyCpu: number;
  transparencyGpu: number;
  taaCpu: number;
  taaGpu: number;
  bloomCpu: number;
  bloomGpu: number;
  fxaaCpu: number;
  fxaaGpu: number;
  overlayQuadCpu: number;
  overlayQuadGpu: number;
  overlayDebugCpu: number;
  overlayDebugGpu: number;
  // Whole-frame GPU time from a single TIME_ELAPSED query on alternating
  // frames (per-pass and frame timers can't nest). Trustworthy on macOS
  // ANGLE/Metal where per-pass values are inflated; matches Σ(per-pass) on
  // well-behaved drivers.
  frameGpu: number;
  // Counts.
  visibleLights: number;
  visibleObjects: number;
  drawCalls: number;
  instancedDrawCalls: number;
  instancedObjects: number;
  shaderSwitches: number;
  renderedFaces: number;
  // Bitmask from CascadedShadowMap.lastStaticCacheRebuildMask. Per-frame —
  // averaged into rebuild rates per cascade by the overlay.
  csmStaticRebuildMask: number;
};

type RunFlags = {
  shadow: boolean;
  gbuffer: boolean;
  ssao: boolean;
  lighting: boolean;
  volumetric: boolean;
  godRays: boolean;
  aerialPerspective: boolean;
  cloudVolumes: boolean;
  transparency: boolean;
  taa: boolean;
  bloom: boolean;
  fxaa: boolean;
  overlayQuad: boolean;
  overlayDebug: boolean;
};

type Averaged = Sample & {
  fps: number;
  cpuTotal: number;
  gpuTotal: number;
  // Number of frames in the rolling window where each CSM cascade rebuilt
  // its static cache. `[N, N, N, N]` over a 30-frame window = full rebuild
  // every frame → the cascade-center texel-drift threshold is too tight
  // for the current camera speed.
  csmRebuildCounts: number[];
  // Window length used as the denominator for csmRebuildCounts.
  csmRebuildWindow: number;
  // Aggregate of browser-reported "long tasks" (≥50 ms main-thread blocks)
  // over the recent ~2 s window. Counts the blocking events the per-pass
  // timers structurally can't see (microtask drains, gl.bufferData uploads,
  // GC, async streamer continuations).
  longTaskCount: number;
  longTaskMaxMs: number;
  longTaskTotalMs: number;
  longTaskSupported: boolean;
  // Real wall-clock time between rAF callbacks (averaged). Includes
  // microtask drains, GPU stalls, GC pauses and browser layout/paint —
  // anything `cpuTotal`/`gpuTotal` can't see. fps is derived from this.
  wallMs: number;
  // Max wall-clock frame time over the rolling window. This is the spike
  // signal: avg can stay low while one frame in 30 takes 100 ms.
  wallMaxMs: number;
  // wallMs − max(cpuTotal, gpuTotal). At v-sync this is mostly idle wait;
  // a sustained large gap with low cpu/gpu means hidden work (microtask
  // drains, GPU upload stalls, GC) — but read it together with wallMaxMs
  // since vsync floors gap at ~16 − cpu when frames complete on time.
  gapMs: number;
  ran: RunFlags;
};

@Component({
  selector: 'app-perf-overlay',
  templateUrl: './perf-overlay.html',
  styleUrl: './perf-overlay.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PerfOverlay implements OnInit {
  @Input({ required: true }) engine!: Engine;
  @Input({ required: true }) scene!: Scene;

  private destroyRef = inject(DestroyRef);

  private readonly snapshot = signal<Averaged | null>(null);
  readonly collapsed = signal(true);

  // Rolling ring of per-frame samples — averaging over ~30 frames smooths
  // jitter and prevents passes from flickering to '—' on the odd frame
  // where their CPU time rounds to 0.
  private samples: Sample[] = [];
  private sampleWriteIndex = 0;
  private frameTimes: number[] = [];
  private lastSampleTime = performance.now();
  private rafHandle = 0;

  // Long-task ring buffer. Browser-reported main-thread blocks ≥50 ms (or
  // whatever the platform threshold is). Catches spikes that happen *between*
  // rAFs — async tile-streamer continuations, gl.bufferData uploads, GC pauses
  // — none of which any per-pass timer can see.
  private longTasks: { duration: number; at: number }[] = [];
  private longTaskObserver: PerformanceObserver | null = null;
  private static readonly LONG_TASK_WINDOW_MS = 2000;

  ngOnInit(): void {
    // Detect macOS/Metal once for the GPU source label, and seed the mode
    // signal from the timer's actual state.
    const renderer = this.engine.renderer;
    if (renderer instanceof CompositeSceneRenderer) {
      const timer = renderer.deferredRenderer.timer;
      this.perPassUnreliable.set(timer.perPassUnreliable);
      this.gpuMode.set(timer.getMode());
    }

    const tick = () => {
      this.collectSample();
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);

    interval(REFRESH_INTERVAL_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refresh());

    if (typeof PerformanceObserver !== 'undefined') {
      try {
        this.longTaskObserver = new PerformanceObserver((list) => {
          const now = performance.now();
          for (const entry of list.getEntries()) {
            this.longTasks.push({ duration: entry.duration, at: entry.startTime });
          }
          // Drop entries older than the window so the readout reflects "recent"
          // blocking, not the whole session.
          const cutoff = now - PerfOverlay.LONG_TASK_WINDOW_MS;
          while (this.longTasks.length > 0 && this.longTasks[0].at < cutoff) {
            this.longTasks.shift();
          }
        });
        this.longTaskObserver.observe({ type: 'longtask', buffered: true });
      } catch {
        // longtask API not supported in this browser — leave observer null.
        this.longTaskObserver = null;
      }
    }

    this.destroyRef.onDestroy(() => {
      cancelAnimationFrame(this.rafHandle);
      this.longTaskObserver?.disconnect();
    });
  }

  private collectSample(): void {
    const renderer = this.engine.renderer;
    let deferred: DeferredSceneRenderer | undefined;
    if (renderer instanceof CompositeSceneRenderer) deferred = renderer.deferredRenderer;
    if (!deferred) return;

    const now = performance.now();
    const dt = now - this.lastSampleTime;
    this.lastSampleTime = now;
    this.pushFrameTime(dt);

    const r = deferred.renderStats;
    const camera = this.engine.activeCamera;
    const visibleLights = this.scene.lightManager.getVisibleLights(camera.frustum).length;
    const visibleObjectsList = this.scene.objectManager.getAllVisibleObjects(
      RenderMode.GBuffer,
      camera.frustum,
    );
    const visibleObjects = visibleObjectsList.length;
    // RenderGroups are a single octree entry but represent instanceCount instances each.
    // Expand them so the formula (expanded - instancedObjects + instancedDrawCalls) stays positive.
    const expandedVisibleCount = visibleObjectsList.reduce(
      (sum, obj) => sum + (obj instanceof RenderGroup ? obj.instanceCount : 1),
      0,
    );
    const drawCalls =
      expandedVisibleCount - (r.instancedObjects ?? 0) + (r.instancedDrawCalls ?? 0);

    const sample: Sample = {
      tickCpu: r.tickMs,
      tickEngineCpu: r.tickBreakdown.engine,
      tickLightAnimCpu: r.tickBreakdown.lightAnim,
      tickObjAnimCpu: r.tickBreakdown.objAnim,
      tickFeaturesCpu: r.tickBreakdown.features,
      tickDecalsCpu: r.tickBreakdown.decals,
      tickPhysicsCpu: r.tickBreakdown.physics,
      tickTerrainStreamCpu: r.tickBreakdown.terrainStream,
      tickObjectStreamCpu: r.tickBreakdown.objectStream,
      tickSceneOtherCpu: r.tickBreakdown.sceneOther,
      cullingCpu: r.cullingMs,
      instancePrepCpu: r.instancePrepMs,
      tileAssignCpu: r.tileAssignMs,
      shadowCpu: r.shadowCpuMs,
      shadowGpu: r.shadowGpuMs,
      gbufferCpu: r.gbufferMs,
      gbufferGpu: r.gbufferGpuMs,
      ssaoCpu: r.ssaoMs,
      ssaoGpu: r.ssaoGpuMs,
      lightingCpu: r.lightingMs,
      lightingGpu: r.lightingGpuMs,
      volumetricCpu: r.volumetricMs,
      volumetricGpu: r.volumetricGpuMs,
      godRaysCpu: r.godRaysMs,
      godRaysGpu: r.godRaysGpuMs,
      aerialPerspectiveCpu: r.aerialPerspectiveMs,
      aerialPerspectiveGpu: r.aerialPerspectiveGpuMs,
      cloudVolumesCpu: r.cloudVolumesMs,
      cloudVolumesGpu: r.cloudVolumesGpuMs,
      transparencyCpu: r.transparencyMs,
      transparencyGpu: r.transparencyGpuMs,
      taaCpu: r.taaMs,
      taaGpu: r.taaGpuMs,
      bloomCpu: r.bloomMs,
      bloomGpu: r.bloomGpuMs,
      fxaaCpu: r.fxaaMs,
      fxaaGpu: r.fxaaGpuMs,
      overlayQuadCpu: r.overlayQuadMs,
      overlayQuadGpu: r.overlayQuadGpuMs,
      overlayDebugCpu: r.overlayDebugMs,
      overlayDebugGpu: r.overlayDebugGpuMs,
      frameGpu: r.frameGpuMs,
      visibleLights,
      visibleObjects,
      drawCalls,
      instancedDrawCalls: r.instancedDrawCalls,
      instancedObjects: r.instancedObjects,
      shaderSwitches: r.shaderSwitches,
      renderedFaces: r.renderedFaces,
      csmStaticRebuildMask: r.csmStaticRebuildMask,
    };

    this.samples[this.sampleWriteIndex] = sample;
    this.sampleWriteIndex = (this.sampleWriteIndex + 1) % ROLLING_WINDOW_FRAMES;
  }

  private pushFrameTime(dt: number): void {
    if (this.frameTimes.length >= ROLLING_WINDOW_FRAMES) this.frameTimes.shift();
    this.frameTimes.push(dt);
  }

  private refresh(): void {
    if (this.samples.length === 0 || this.frameTimes.length === 0) {
      this.snapshot.set(null);
      return;
    }

    const avgFrameMs = this.frameTimes.reduce((a, b) => a + b, 0) / this.frameTimes.length;
    const maxFrameMs = this.frameTimes.reduce((m, b) => (b > m ? b : m), 0);
    const fps = avgFrameMs > 0 ? 1000 / avgFrameMs : 0;

    const n = this.samples.length;
    const sum = this.samples.reduce<Sample>(
      (a, s) => ({
        tickCpu: a.tickCpu + s.tickCpu,
        tickEngineCpu: a.tickEngineCpu + s.tickEngineCpu,
        tickLightAnimCpu: a.tickLightAnimCpu + s.tickLightAnimCpu,
        tickObjAnimCpu: a.tickObjAnimCpu + s.tickObjAnimCpu,
        tickFeaturesCpu: a.tickFeaturesCpu + s.tickFeaturesCpu,
        tickDecalsCpu: a.tickDecalsCpu + s.tickDecalsCpu,
        tickPhysicsCpu: a.tickPhysicsCpu + s.tickPhysicsCpu,
        tickTerrainStreamCpu: a.tickTerrainStreamCpu + s.tickTerrainStreamCpu,
        tickObjectStreamCpu: a.tickObjectStreamCpu + s.tickObjectStreamCpu,
        tickSceneOtherCpu: a.tickSceneOtherCpu + s.tickSceneOtherCpu,
        cullingCpu: a.cullingCpu + s.cullingCpu,
        instancePrepCpu: a.instancePrepCpu + s.instancePrepCpu,
        tileAssignCpu: a.tileAssignCpu + s.tileAssignCpu,
        shadowCpu: a.shadowCpu + s.shadowCpu,
        shadowGpu: a.shadowGpu + s.shadowGpu,
        gbufferCpu: a.gbufferCpu + s.gbufferCpu,
        gbufferGpu: a.gbufferGpu + s.gbufferGpu,
        ssaoCpu: a.ssaoCpu + s.ssaoCpu,
        ssaoGpu: a.ssaoGpu + s.ssaoGpu,
        lightingCpu: a.lightingCpu + s.lightingCpu,
        lightingGpu: a.lightingGpu + s.lightingGpu,
        volumetricCpu: a.volumetricCpu + s.volumetricCpu,
        volumetricGpu: a.volumetricGpu + s.volumetricGpu,
        godRaysCpu: a.godRaysCpu + s.godRaysCpu,
        godRaysGpu: a.godRaysGpu + s.godRaysGpu,
        aerialPerspectiveCpu: a.aerialPerspectiveCpu + s.aerialPerspectiveCpu,
        aerialPerspectiveGpu: a.aerialPerspectiveGpu + s.aerialPerspectiveGpu,
        cloudVolumesCpu: a.cloudVolumesCpu + s.cloudVolumesCpu,
        cloudVolumesGpu: a.cloudVolumesGpu + s.cloudVolumesGpu,
        transparencyCpu: a.transparencyCpu + s.transparencyCpu,
        transparencyGpu: a.transparencyGpu + s.transparencyGpu,
        taaCpu: a.taaCpu + s.taaCpu,
        taaGpu: a.taaGpu + s.taaGpu,
        bloomCpu: a.bloomCpu + s.bloomCpu,
        bloomGpu: a.bloomGpu + s.bloomGpu,
        fxaaCpu: a.fxaaCpu + s.fxaaCpu,
        fxaaGpu: a.fxaaGpu + s.fxaaGpu,
        overlayQuadCpu: a.overlayQuadCpu + s.overlayQuadCpu,
        overlayQuadGpu: a.overlayQuadGpu + s.overlayQuadGpu,
        overlayDebugCpu: a.overlayDebugCpu + s.overlayDebugCpu,
        overlayDebugGpu: a.overlayDebugGpu + s.overlayDebugGpu,
        frameGpu: a.frameGpu + s.frameGpu,
        visibleLights: a.visibleLights + s.visibleLights,
        visibleObjects: a.visibleObjects + s.visibleObjects,
        drawCalls: a.drawCalls + s.drawCalls,
        instancedDrawCalls: a.instancedDrawCalls + s.instancedDrawCalls,
        instancedObjects: a.instancedObjects + s.instancedObjects,
        shaderSwitches: a.shaderSwitches + s.shaderSwitches,
        renderedFaces: a.renderedFaces + s.renderedFaces,
        // Bitwise OR keeps "ever set this window" — read alongside per-bit
        // counts below to get rebuild rate vs. one-off rebuild.
        csmStaticRebuildMask: a.csmStaticRebuildMask | s.csmStaticRebuildMask,
      }),
      ZERO_SAMPLE,
    );

    // Per-cascade rebuild count over the rolling window.
    const csmRebuildCounts = [0, 0, 0, 0];
    for (const s of this.samples) {
      for (let i = 0; i < 4; i++) {
        if (s.csmStaticRebuildMask & (1 << i)) csmRebuildCounts[i]++;
      }
    }

    const avg: Sample = {
      tickCpu: sum.tickCpu / n,
      tickEngineCpu: sum.tickEngineCpu / n,
      tickLightAnimCpu: sum.tickLightAnimCpu / n,
      tickObjAnimCpu: sum.tickObjAnimCpu / n,
      tickFeaturesCpu: sum.tickFeaturesCpu / n,
      tickDecalsCpu: sum.tickDecalsCpu / n,
      tickPhysicsCpu: sum.tickPhysicsCpu / n,
      tickTerrainStreamCpu: sum.tickTerrainStreamCpu / n,
      tickObjectStreamCpu: sum.tickObjectStreamCpu / n,
      tickSceneOtherCpu: sum.tickSceneOtherCpu / n,
      cullingCpu: sum.cullingCpu / n,
      instancePrepCpu: sum.instancePrepCpu / n,
      tileAssignCpu: sum.tileAssignCpu / n,
      shadowCpu: sum.shadowCpu / n,
      shadowGpu: sum.shadowGpu / n,
      gbufferCpu: sum.gbufferCpu / n,
      gbufferGpu: sum.gbufferGpu / n,
      ssaoCpu: sum.ssaoCpu / n,
      ssaoGpu: sum.ssaoGpu / n,
      lightingCpu: sum.lightingCpu / n,
      lightingGpu: sum.lightingGpu / n,
      volumetricCpu: sum.volumetricCpu / n,
      volumetricGpu: sum.volumetricGpu / n,
      godRaysCpu: sum.godRaysCpu / n,
      godRaysGpu: sum.godRaysGpu / n,
      aerialPerspectiveCpu: sum.aerialPerspectiveCpu / n,
      aerialPerspectiveGpu: sum.aerialPerspectiveGpu / n,
      cloudVolumesCpu: sum.cloudVolumesCpu / n,
      cloudVolumesGpu: sum.cloudVolumesGpu / n,
      transparencyCpu: sum.transparencyCpu / n,
      transparencyGpu: sum.transparencyGpu / n,
      taaCpu: sum.taaCpu / n,
      taaGpu: sum.taaGpu / n,
      bloomCpu: sum.bloomCpu / n,
      bloomGpu: sum.bloomGpu / n,
      fxaaCpu: sum.fxaaCpu / n,
      fxaaGpu: sum.fxaaGpu / n,
      overlayQuadCpu: sum.overlayQuadCpu / n,
      overlayQuadGpu: sum.overlayQuadGpu / n,
      overlayDebugCpu: sum.overlayDebugCpu / n,
      overlayDebugGpu: sum.overlayDebugGpu / n,
      frameGpu: sum.frameGpu / n,
      visibleLights: sum.visibleLights / n,
      visibleObjects: sum.visibleObjects / n,
      drawCalls: sum.drawCalls / n,
      instancedDrawCalls: sum.instancedDrawCalls / n,
      instancedObjects: sum.instancedObjects / n,
      shaderSwitches: sum.shaderSwitches / n,
      renderedFaces: sum.renderedFaces / n,
      // Carry the OR-mask through; per-cascade rebuild rate goes into the
      // snapshot below for display.
      csmStaticRebuildMask: sum.csmStaticRebuildMask,
    };

    // tileAssignCpu is intentionally NOT summed — it's a strict subset of
    // lightingCpu and would double-count the frame total.
    const cpuTotal =
      avg.tickCpu +
      avg.cullingCpu +
      avg.instancePrepCpu +
      avg.shadowCpu +
      avg.gbufferCpu +
      avg.ssaoCpu +
      avg.lightingCpu +
      avg.volumetricCpu +
      avg.godRaysCpu +
      avg.aerialPerspectiveCpu +
      avg.cloudVolumesCpu +
      avg.transparencyCpu +
      avg.taaCpu +
      avg.bloomCpu +
      avg.fxaaCpu +
      avg.overlayQuadCpu +
      avg.overlayDebugCpu;
    const gpuSumPasses =
      avg.shadowGpu +
      avg.gbufferGpu +
      avg.ssaoGpu +
      avg.lightingGpu +
      avg.volumetricGpu +
      avg.godRaysGpu +
      avg.aerialPerspectiveGpu +
      avg.cloudVolumesGpu +
      avg.transparencyGpu +
      avg.taaGpu +
      avg.bloomGpu +
      avg.fxaaGpu +
      avg.overlayQuadGpu +
      avg.overlayDebugGpu;
    // Prefer the single frame-wide TIME_ELAPSED measurement when present.
    // It's the trust signal on macOS Metal where per-pass sum is unreliable;
    // on well-behaved drivers it should match gpuSumPasses closely.
    const gpuTotal = avg.frameGpu > 0 ? avg.frameGpu : gpuSumPasses;

    // CPU and GPU pipeline in parallel — the GPU executes commands the CPU
    // queued earlier — so the instrumented frame cost is bounded by whichever
    // side is slower, not their sum. wallMs is the actual rAF-to-rAF delta;
    // gapMs is the slice we *can't* attribute to any timed pass.
    const wallMs = avgFrameMs;
    const wallMaxMs = maxFrameMs;
    const gapMs = Math.max(0, wallMs - Math.max(cpuTotal, gpuTotal));

    // Read the latest run-flags directly from the renderer. These are
    // deterministic (set every frame based on settings + sun-gating + TAA/
    // FXAA exclusion) so no averaging is needed.
    const renderer = this.engine.renderer;
    let deferred: DeferredSceneRenderer | undefined;
    if (renderer instanceof CompositeSceneRenderer) deferred = renderer.deferredRenderer;
    const r = deferred?.renderStats;
    const ran: RunFlags = {
      shadow: r?.shadowRan ?? false,
      gbuffer: r?.gbufferRan ?? false,
      ssao: r?.ssaoRan ?? false,
      lighting: r?.lightingRan ?? false,
      volumetric: r?.volumetricRan ?? false,
      godRays: r?.godRaysRan ?? false,
      aerialPerspective: r?.aerialPerspectiveRan ?? false,
      cloudVolumes: r?.cloudVolumesRan ?? false,
      transparency: r?.transparencyRan ?? false,
      taa: r?.taaRan ?? false,
      bloom: r?.bloomRan ?? false,
      fxaa: r?.fxaaRan ?? false,
      // Overlay passes have no run-flag — they always run when objects exist.
      // Use averaged CPU > 0 as the "active" signal so they don't show '—'.
      overlayQuad: avg.overlayQuadCpu > 0,
      overlayDebug: avg.overlayDebugCpu > 0,
    };

    // Drop stale long-task entries before reading aggregates.
    const cutoff = performance.now() - PerfOverlay.LONG_TASK_WINDOW_MS;
    while (this.longTasks.length > 0 && this.longTasks[0].at < cutoff) {
      this.longTasks.shift();
    }
    let longTaskMaxMs = 0;
    let longTaskTotalMs = 0;
    for (const t of this.longTasks) {
      if (t.duration > longTaskMaxMs) longTaskMaxMs = t.duration;
      longTaskTotalMs += t.duration;
    }

    this.snapshot.set({
      ...avg,
      fps,
      cpuTotal,
      gpuTotal,
      wallMs,
      wallMaxMs,
      gapMs,
      csmRebuildCounts,
      csmRebuildWindow: this.samples.length,
      longTaskCount: this.longTasks.length,
      longTaskMaxMs,
      longTaskTotalMs,
      longTaskSupported: this.longTaskObserver !== null,
      ran,
    });
  }

  toggleCollapsed(): void {
    this.collapsed.update((v) => !v);
  }

  readonly fpsText = computed(() => {
    const s = this.snapshot();
    return s ? s.fps.toFixed(0) : '—';
  });

  readonly cpuTotalText = computed(() => {
    const s = this.snapshot();
    return s ? s.cpuTotal.toFixed(1) : '—';
  });

  readonly gpuTotalText = computed(() => {
    const s = this.snapshot();
    return s ? s.gpuTotal.toFixed(1) : '—';
  });

  // Source label for the GPU total. Composed of the active timer mode
  // (per-pass / frame / alternate) and the value source for *this* frame
  // (frame query vs. summed per-pass). Click to cycle through modes.
  readonly gpuSourceText = computed(() => {
    const s = this.snapshot();
    if (!s) return '';
    const mode = this.gpuMode();
    const fromFrame = s.frameGpu > 0;
    if (mode === 'frame') return '(frame)';
    if (mode === 'per-pass') {
      return this.perPassUnreliable() ? '(Σ, unreliable on Metal)' : '(Σ)';
    }
    // alternate — value alternates frame-to-frame; tag what we're showing now
    return fromFrame ? '(alt: frame)' : '(alt: Σ)';
  });

  // Active GpuTimer mode, kept in sync with the timer's own state. Display
  // and cycling both go through this signal so the label updates immediately
  // on click without waiting for the next refresh tick.
  private readonly gpuMode = signal<GpuTimingMode>('alternate');

  // Lazily read from the engine's GPU timer after ngOnInit binds `engine`.
  // The renderer string is stable per WebGL context, so one read suffices.
  private readonly perPassUnreliable = signal(false);

  // Click handler for the GPU source label. Cycles alternate → per-pass →
  // frame → alternate. Stored on the renderer's timer so the choice survives
  // overlay teardown (e.g. if the HUD is recreated on resize).
  cycleGpuMode(): void {
    const renderer = this.engine.renderer;
    if (!(renderer instanceof CompositeSceneRenderer)) return;
    const timer = renderer.deferredRenderer.timer;
    const next: Record<GpuTimingMode, GpuTimingMode> = {
      alternate: 'per-pass',
      'per-pass': 'frame',
      frame: 'alternate',
    };
    const newMode = next[timer.getMode()];
    timer.setMode(newMode);
    this.gpuMode.set(newMode);
  }

  readonly wallMsText = computed(() => {
    const s = this.snapshot();
    return s ? s.wallMs.toFixed(1) : '—';
  });

  readonly wallMaxMsText = computed(() => {
    const s = this.snapshot();
    return s ? s.wallMaxMs.toFixed(1) : '—';
  });

  readonly gapMsText = computed(() => {
    const s = this.snapshot();
    return s ? s.gapMs.toFixed(1) : '—';
  });

  // Rendered pixel count: WxH (Nm). Read live from the viewport so it tracks
  // renderScale changes and window resizes. Megapixel total is the headline
  // — fragment passes scale near-linearly with it, so this row is the
  // fastest way to see the cost of moving the render-scale slider.
  readonly pixelsText = computed(() => {
    // Re-read every refresh so resize / preset changes are reflected.
    this.snapshot();
    const vp = this.engine.ctx.viewPort;
    const w = vp.width;
    const h = vp.height;
    const mp = (w * h) / 1_000_000;
    return `${w}×${h} (${mp.toFixed(2)}m)`;
  });

  readonly passes = computed<PassRow[]>(() => {
    const s = this.snapshot();
    if (!s) return [];
    // Active is read directly from the renderer's per-pass run flags rather
    // than inferred from timing. Cheap passes can register CPU=0 (sub-tick),
    // and GPU timer queries lag 1-3 frames, so timing-derived active flickers.
    // Tick / Culling / Instance Prep run every frame and have no GPU side, so
    // they are always active and show '—' for GPU.
    return [
      { name: 'Tick', cpu: s.tickCpu, gpu: 0, active: true, cpuOnly: true },
      {
        name: '  ↳ engine',
        cpu: s.tickEngineCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ lightAnim',
        cpu: s.tickLightAnimCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ objAnim',
        cpu: s.tickObjAnimCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ features',
        cpu: s.tickFeaturesCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ decals',
        cpu: s.tickDecalsCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ physics',
        cpu: s.tickPhysicsCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ terrainStream',
        cpu: s.tickTerrainStreamCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ objectStream',
        cpu: s.tickObjectStreamCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      {
        name: '  ↳ sceneOther',
        cpu: s.tickSceneOtherCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      { name: 'Culling', cpu: s.cullingCpu, gpu: 0, active: true, cpuOnly: true },
      {
        name: 'Instance Prep',
        cpu: s.instancePrepCpu,
        gpu: 0,
        active: true,
        cpuOnly: true,
      },
      { name: 'Shadow', cpu: s.shadowCpu, gpu: s.shadowGpu, active: s.ran.shadow },
      { name: 'G-Buffer', cpu: s.gbufferCpu, gpu: s.gbufferGpu, active: s.ran.gbuffer },
      { name: 'SSAO', cpu: s.ssaoCpu, gpu: s.ssaoGpu, active: s.ran.ssao },
      { name: 'Lighting', cpu: s.lightingCpu, gpu: s.lightingGpu, active: s.ran.lighting },
      {
        name: '  ↳ Tile Assign',
        cpu: s.tileAssignCpu,
        gpu: 0,
        active: s.ran.lighting,
        cpuOnly: true,
      },
      {
        name: 'Volumetric',
        cpu: s.volumetricCpu,
        gpu: s.volumetricGpu,
        active: s.ran.volumetric,
      },
      { name: 'God Rays', cpu: s.godRaysCpu, gpu: s.godRaysGpu, active: s.ran.godRays },
      {
        name: 'Aerial Persp.',
        cpu: s.aerialPerspectiveCpu,
        gpu: s.aerialPerspectiveGpu,
        active: s.ran.aerialPerspective,
      },
      {
        name: 'Cloud Volumes',
        cpu: s.cloudVolumesCpu,
        gpu: s.cloudVolumesGpu,
        active: s.ran.cloudVolumes,
      },
      {
        name: 'Transparent',
        cpu: s.transparencyCpu,
        gpu: s.transparencyGpu,
        active: s.ran.transparency,
      },
      { name: 'TAA', cpu: s.taaCpu, gpu: s.taaGpu, active: s.ran.taa },
      { name: 'Bloom', cpu: s.bloomCpu, gpu: s.bloomGpu, active: s.ran.bloom },
      { name: 'FXAA', cpu: s.fxaaCpu, gpu: s.fxaaGpu, active: s.ran.fxaa },
      {
        name: 'Overlay Quad',
        cpu: s.overlayQuadCpu,
        gpu: s.overlayQuadGpu,
        active: s.ran.overlayQuad,
      },
      {
        name: 'Overlay Debug',
        cpu: s.overlayDebugCpu,
        gpu: s.overlayDebugGpu,
        active: s.ran.overlayDebug,
      },
    ];
  });

  readonly counts = computed(() => {
    const s = this.snapshot();
    if (!s) return null;
    return {
      lights: Math.round(s.visibleLights),
      objects: Math.round(s.visibleObjects),
      draws: Math.round(s.drawCalls),
      instanced: Math.round(s.instancedDrawCalls),
      instances: Math.round(s.instancedObjects),
      shaderSwitches: Math.round(s.shaderSwitches),
      faces: formatFaces(Math.round(s.renderedFaces)),
      csmRebuilds: s.csmRebuildCounts.map((c) => `${c}/${s.csmRebuildWindow}`).join('  '),
      longTasks: s.longTaskSupported
        ? `${s.longTaskCount} (max ${s.longTaskMaxMs.toFixed(0)} ms, ` +
          `Σ ${s.longTaskTotalMs.toFixed(0)} ms / 2 s)`
        : 'unsupported',
    };
  });
}

function formatFaces(n: number): string {
  if (n < 1000) return '< 1K';
  if (n < 1_000_000) return `${Math.round(n / 1000)}K`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

const ZERO_SAMPLE: Sample = {
  tickCpu: 0,
  tickEngineCpu: 0,
  tickLightAnimCpu: 0,
  tickObjAnimCpu: 0,
  tickFeaturesCpu: 0,
  tickDecalsCpu: 0,
  tickPhysicsCpu: 0,
  tickTerrainStreamCpu: 0,
  tickObjectStreamCpu: 0,
  tickSceneOtherCpu: 0,
  cullingCpu: 0,
  instancePrepCpu: 0,
  tileAssignCpu: 0,
  shadowCpu: 0,
  shadowGpu: 0,
  gbufferCpu: 0,
  gbufferGpu: 0,
  ssaoCpu: 0,
  ssaoGpu: 0,
  lightingCpu: 0,
  lightingGpu: 0,
  volumetricCpu: 0,
  volumetricGpu: 0,
  godRaysCpu: 0,
  godRaysGpu: 0,
  aerialPerspectiveCpu: 0,
  aerialPerspectiveGpu: 0,
  cloudVolumesCpu: 0,
  cloudVolumesGpu: 0,
  transparencyCpu: 0,
  transparencyGpu: 0,
  taaCpu: 0,
  taaGpu: 0,
  bloomCpu: 0,
  bloomGpu: 0,
  fxaaCpu: 0,
  fxaaGpu: 0,
  overlayQuadCpu: 0,
  overlayQuadGpu: 0,
  overlayDebugCpu: 0,
  overlayDebugGpu: 0,
  frameGpu: 0,
  visibleLights: 0,
  visibleObjects: 0,
  drawCalls: 0,
  instancedDrawCalls: 0,
  instancedObjects: 0,
  shaderSwitches: 0,
  renderedFaces: 0,
  csmStaticRebuildMask: 0,
};
